import config from '../config';
import Cookies from 'js-cookie';
import { filterActivePromotions } from '../utils/promotionUtils';

// Enhanced caching configuration for web
const CACHE_CONFIG = {
  DASHBOARD_TTL: 5 * 60 * 1000, // 5 minutes
  PROMOTED_TTL: 10 * 60 * 1000, // 10 minutes
  CATEGORY_TTL: 15 * 60 * 1000, // 15 minutes
  USER_DATA_TTL: 30 * 60 * 1000, // 30 minutes
  PROVINCE_TTL: 10 * 60 * 1000, // 10 minutes for province-specific data
  MAX_CACHE_SIZE: 100, // Max items per cache type
};

// Request deduplication cache
const requestCache = new Map();

// Memory cache for frequently accessed data
const memoryCache = new Map();

// Utility function for cache key generation
const generateCacheKey = (endpoint, params = {}) => {
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}=${params[key]}`)
    .join('&');
  return `${endpoint}${sortedParams ? `?${sortedParams}` : ''}`;
};

// Memory cache utilities
const setMemoryCache = (key, data, ttl = CACHE_CONFIG.DASHBOARD_TTL) => {
  memoryCache.set(key, { data, timestamp: Date.now(), ttl });
  
  // Clean up old entries if cache is too large
  if (memoryCache.size > CACHE_CONFIG.MAX_CACHE_SIZE) {
    const entries = Array.from(memoryCache.entries());
    entries.sort(([,a], [,b]) => a.timestamp - b.timestamp);
    const toDelete = entries.slice(0, entries.length - CACHE_CONFIG.MAX_CACHE_SIZE);
    toDelete.forEach(([key]) => memoryCache.delete(key));
  }
};

const getMemoryCache = (key) => {
  const cached = memoryCache.get(key);
  if (!cached) return null;
  
  if (Date.now() - cached.timestamp > cached.ttl) {
    memoryCache.delete(key);
    return null;
  }
  
  return cached.data;
};

// Request deduplication wrapper
const deduplicateRequest = async (key, requestFn) => {
  if (requestCache.has(key)) {return requestCache.get(key);
  }
  
  const promise = requestFn().finally(() => {
    requestCache.delete(key);
  });
  
  requestCache.set(key, promise);
  return promise;
};

// Local storage utilities for persistent caching
const setLocalCache = (key, data, ttl = CACHE_CONFIG.DASHBOARD_TTL) => {
  try {
    const cacheData = {
      data,
      timestamp: Date.now(),
      ttl
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (error) {}
};

const getLocalCache = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    
    const { data, timestamp, ttl } = JSON.parse(cached);
    if (Date.now() - timestamp > ttl) {
      localStorage.removeItem(key);
      return null;
    }
    
    return data;
  } catch (error) {return null;
  }
};

/**
 * OPTIMIZED: Enhanced ProductService for web with caching and parallel requests
 */
class ProductService {
  /**
   * OPTIMIZED: Combined dashboard products with promoted products in parallel
   */
  static async getDashboardProductsCombined(page = 1, pageSize = 30) {
    const startTime = Date.now();
    const cacheKey = generateCacheKey('dashboard_combined', { page, pageSize });try {
      // 1. Check memory cache first
      const cachedData = getMemoryCache(cacheKey);
      if (cachedData) {// Normalize cached shape: ensure `products` exists
        const normalized = { ...cachedData };
        if (!Array.isArray(normalized.products) && Array.isArray(normalized.categories)) {
          normalized.products = normalized.categories.flatMap(cat => cat.products || []);
        }

        // Update memory cache with normalized shape for consistency
        setMemoryCache(cacheKey, normalized, CACHE_CONFIG.DASHBOARD_TTL);

        return { ...normalized, fromCache: true, loadTime: Date.now() - startTime };
      }

      // 2. Check local storage cache
      const localCachedData = getLocalCache(cacheKey);
      if (localCachedData) {
        // Normalize local cached shape: ensure `products` exists
        const normalizedLocal = { ...localCachedData };
        if (!Array.isArray(normalizedLocal.products) && Array.isArray(normalizedLocal.categories)) {
          normalizedLocal.products = normalizedLocal.categories.flatMap(cat => cat.products || []);
        }

        // Set in memory cache for faster access next time
        setMemoryCache(cacheKey, normalizedLocal, CACHE_CONFIG.DASHBOARD_TTL);
        
        // Start background refresh
        this.refreshDashboardInBackground(page, pageSize, cacheKey);
        
        return { ...normalizedLocal, fromCache: true, loadTime: Date.now() - startTime };
      }

      // 3. Fetch from network with parallel requests
      const [productsData, promotedData] = await Promise.all([
        deduplicateRequest(
          `dashboard_products_p${page}`, 
          () => this.fetchDashboardProductsFromNetwork(page, pageSize)
        ),
        deduplicateRequest(
          'promoted_products', 
          () => this.fetchPromotedProductsFromNetwork()
        )
      ]);
      
      if (productsData) {
        const combinedData = {
          products: productsData.results || [],
          promotedProducts: promotedData?.results || [],
          hasMore: !!productsData.next,
          totalCount: productsData.count || 0,
        };
        
        // Cache the results
        setMemoryCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);
        setLocalCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);return {
          ...combinedData,
          fromCache: false,
          loadTime: Date.now() - startTime
        };
      }

      return { 
        products: [], 
        promotedProducts: [], 
        hasMore: false, 
        fromCache: false, 
        totalCount: 0,
        loadTime: Date.now() - startTime
      };
    } catch (error) {
      console.error('ProductService.getDashboardProductsCombined: Error', error);
      
      // Fallback to any cached data
      const fallbackData = getLocalCache(cacheKey);
      return {
        products: fallbackData?.products || [],
        promotedProducts: fallbackData?.promotedProducts || [],
        hasMore: false,
        fromCache: true,
        totalCount: 0,
        loadTime: Date.now() - startTime
      };
    }
  }

  /**
   * OPTIMIZED: Combined products by province/city with promoted products
   */
  static async getProductsByProvinceCombined(cityId = '', page = 1, pageSize = 30) {
    const startTime = Date.now();
    const cacheKey = generateCacheKey('province_combined', { cityId, page, pageSize });try {
      // Check memory cache
      const cachedData = getMemoryCache(cacheKey);
      if (cachedData) {
        return { ...cachedData, fromCache: true, loadTime: Date.now() - startTime };
      }

      // Check local storage
      const localCachedData = getLocalCache(cacheKey);
      if (localCachedData) {
        setMemoryCache(cacheKey, localCachedData, CACHE_CONFIG.DASHBOARD_TTL);
        this.refreshProvinceInBackground(cityId, page, pageSize, cacheKey);
        return { ...localCachedData, fromCache: true, loadTime: Date.now() - startTime };
      }

      // Fetch from network with parallel requests
      const [productsData, promotedData] = await Promise.all([
        deduplicateRequest(
          `province_products_${cityId}_p${page}`, 
          () => this.fetchProductsByProvinceFromNetwork(cityId, page, pageSize)
        ),
        deduplicateRequest(
          'promoted_user_city', 
          () => this.fetchPromotedProductsByCityFromNetwork()
        )
      ]);
      
      if (productsData) {
        const combinedData = {
          products: productsData.products || [],
          promotedProducts: promotedData?.results || [],
          cities: productsData.cities || [],
          currentProvince: productsData.current_province || '',
          hasMore: productsData.hasMore || false,
          totalCount: productsData.products?.length || 0,
        };
        
        setMemoryCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);
        setLocalCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);
        
        return {
          ...combinedData,
          fromCache: false,
          loadTime: Date.now() - startTime
        };
      }

      return { 
        products: [], 
        promotedProducts: [], 
        cities: [],
        currentProvince: '',
        hasMore: false, 
        fromCache: false, 
        totalCount: 0,
        loadTime: Date.now() - startTime
      };
    } catch (error) {
      console.error('ProductService.getProductsByProvinceCombined: Error', error);
      const fallbackData = getLocalCache(cacheKey);
      return {
        products: fallbackData?.products || [],
        promotedProducts: fallbackData?.promotedProducts || [],
        cities: fallbackData?.cities || [],
        currentProvince: fallbackData?.currentProvince || '',
        hasMore: false,
        fromCache: true,
        totalCount: 0,
        loadTime: Date.now() - startTime
      };
    }
  }

  /**
   * OPTIMIZED: Combined products by category with promoted products  
   */
  static async getProductsByCategoryCombined(category, page = 1, pageSize = 30) {
    const startTime = Date.now();
    const cacheKey = generateCacheKey('category_combined', { category, page, pageSize });try {
      // Check memory cache
      const cachedData = getMemoryCache(cacheKey);
      if (cachedData) {
        return { ...cachedData, fromCache: true, loadTime: Date.now() - startTime };
      }

      // Check local storage
      const localCachedData = getLocalCache(cacheKey);
      if (localCachedData) {
        setMemoryCache(cacheKey, localCachedData, CACHE_CONFIG.CATEGORY_TTL);
        this.refreshCategoryInBackground(category, page, pageSize, cacheKey);
        return { ...localCachedData, fromCache: true, loadTime: Date.now() - startTime };
      }

      // Fetch from network with parallel requests
      const [productsData, promotedData] = await Promise.all([
        deduplicateRequest(
          `category_products_${category}_p${page}`, 
          () => this.fetchProductsByCategoryFromNetwork(category, page, pageSize)
        ),
        deduplicateRequest(
          `promoted_category_${category}`, 
          () => this.fetchPromotedProductsByCategoryFromNetwork(category)
        )
      ]);
      
      if (productsData) {
        const combinedData = {
          products: productsData.results || [],
          promotedProducts: promotedData?.results || [],
          hasMore: !!productsData.next,
          totalCount: productsData.count || 0,
        };
        
        setMemoryCache(cacheKey, combinedData, CACHE_CONFIG.CATEGORY_TTL);
        setLocalCache(cacheKey, combinedData, CACHE_CONFIG.CATEGORY_TTL);
        
        return {
          ...combinedData,
          fromCache: false,
          loadTime: Date.now() - startTime
        };
      }

      return { 
        products: [], 
        promotedProducts: [], 
        hasMore: false, 
        fromCache: false, 
        totalCount: 0,
        loadTime: Date.now() - startTime
      };
    } catch (error) {
      console.error('ProductService.getProductsByCategoryCombined: Error', error);
      return {
        products: [],
        promotedProducts: [],
        hasMore: false,
        fromCache: false,
        totalCount: 0,
        loadTime: Date.now() - startTime
      };
    }
  }

  /**
   * OPTIMIZED: Combined user province products with promoted products
   */
  static async getUserProvinceCombined(cityId = '', page = 1, pageSize = 30) {
    const cacheKey = generateCacheKey('user_province_combined', { cityId, page, pageSize });// Check memory cache first
    const memoryCached = getMemoryCache(cacheKey);
    if (memoryCached) {return { ...memoryCached, fromCache: true };
    }
    
    // Check localStorage cache
    const localCachedData = getLocalCache(cacheKey);
    if (localCachedData) {// Set in memory cache for faster future access
      setMemoryCache(cacheKey, localCachedData, CACHE_CONFIG.DASHBOARD_TTL);
      this.refreshUserProvinceInBackground(cityId, page, pageSize, cacheKey);
      return { ...localCachedData, fromCache: true };
    }

    try {
      // Use request deduplication
      const [productsData, promotedData] = await Promise.all([
        this.requestWithDeduplication(
          `user_province_products_${cityId}_p${page}`, 
          () => this.fetchUserProvinceProductsFromNetwork(cityId, page, pageSize)
        ),
        this.requestWithDeduplication(
          `promoted_province_${cityId}`, 
          () => this.fetchPromotedProvinceProductsFromNetwork(cityId)
        )
      ]);
      
      // Return products directly (not grouped by category)
      const combinedData = {
        products: productsData.products || [],
        cities: productsData.cities || [],
        current_province: productsData.current_province || '',
        promotedProducts: promotedData || [],
        totalProducts: productsData.products?.length || 0,
        hasMore: !!productsData.pagination?.has_next || false,
        pagination: productsData.pagination || {}
      };

      // Cache the result
      setMemoryCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);
      setLocalCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);return { ...combinedData, fromCache: false };
    } catch (error) {
      console.error('ProductService.getUserProvinceCombined: Error fetching data', error);
      throw error;
    }
  }

  /**
   * Background refresh for user province data
   */
  static refreshUserProvinceInBackground(cityId, page, pageSize, cacheKey) {
    setTimeout(async () => {
      try {const [productsData, promotedData] = await Promise.all([
          this.fetchUserProvinceProductsFromNetwork(cityId, page, pageSize),
          this.fetchPromotedProvinceProductsFromNetwork(cityId)
        ]);
        
        const combinedData = {
          products: productsData.products || [],
          cities: productsData.cities || [],
          current_province: productsData.current_province || '',
          promotedProducts: promotedData || [],
          totalProducts: productsData.products?.length || 0,
          hasMore: !!productsData.pagination?.has_next || false,
          pagination: productsData.pagination || {}
        };

        setMemoryCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);
        setLocalCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);} catch (error) {}
    }, 100);
  }

  /**
   * Fetch user province products from network
   */
  static async fetchUserProvinceProductsFromNetwork(cityId = '', page = 1, pageSize = 30) {
    const token = Cookies.get('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const params = new URLSearchParams();
    if (cityId) params.append('city', cityId);
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    
    const url = `${config.backendUrl}/products/all/user-province/?${params}`;const response = await fetch(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ProductService: Province products fetch failed:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();return {
      products: data.results || [],
      cities: data.cities || [],
      current_province: data.current_province || '',
      pagination: data.pagination || {},
    };
  }

  /**
   * Fetch promoted products for province from network
   */
  static async fetchPromotedProvinceProductsFromNetwork(cityId = '') {
    try {
      const token = Cookies.get('authToken');
      const headers = token ? { Authorization: `Token ${token}` } : {};
      
      const params = new URLSearchParams();
      if (cityId) params.append('city', cityId);
      
      const url = `${config.backendUrl}/products/promoted/?${params}`;
      const response = await fetch(url, { headers });

      if (!response.ok) {return [];
      }

      return await response.json();
    } catch (error) {return [];
    }
  }
  
  /**
   * Helper method to group products by category
   */
  static groupProductsByCategory(products = []) {
    if (!products || !Array.isArray(products)) {return [];
    }
    
    const categoryMap = new Map();
    
    // First group products by category
    products.forEach(product => {
      const categoryId = product.category || 0;
      const categoryName = product.category_name || 'Uncategorized';
      
      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          id: categoryId,
          name: categoryName,
          products: []
        });
      }
      
      categoryMap.get(categoryId).products.push(product);
    });
    
    // Convert map to array
    return Array.from(categoryMap.values());
  }

  // Background refresh methods
  static async refreshDashboardInBackground(page, pageSize, cacheKey) {
    try {
      const [productsData, promotedData] = await Promise.all([
        this.fetchDashboardProductsFromNetwork(page, pageSize),
        this.fetchPromotedProductsFromNetwork()
      ]);
      
      if (productsData) {
        const productsByCategory = this.groupProductsByCategory(productsData.results || []);
        // Ensure cached shape contains a `products` array for compatibility with callers
        const combinedData = {
          products: productsData.results || [],
          categories: productsByCategory,
          promotedProducts: promotedData?.results || promotedData || [],
          totalProducts: productsData.results?.length || 0,
          hasMore: !!productsData.next,
          totalCount: productsData.count || 0
        };

        setMemoryCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);
        setLocalCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);}
    } catch (error) {}
  }

  static async refreshProvinceInBackground(cityId, page, pageSize, cacheKey) {
    try {
      const [productsData, promotedData] = await Promise.all([
        this.fetchUserProvinceProductsFromNetwork(cityId, page, pageSize),
        this.fetchPromotedProvinceProductsFromNetwork(cityId)
      ]);
      
      if (productsData) {
        const combinedData = {
          products: productsData.products || [],
          cities: productsData.cities || [],  
          current_province: productsData.current_province || '',
          promotedProducts: promotedData || [],
          totalProducts: productsData.products?.length || 0,
          hasMore: !!productsData.pagination?.has_next || false,
          pagination: productsData.pagination || {}
        };
        
        setMemoryCache(cacheKey, combinedData, CACHE_CONFIG.PROVINCE_TTL);
        setLocalCache(cacheKey, combinedData, CACHE_CONFIG.PROVINCE_TTL);}
    } catch (error) {}
  }

  static async refreshCategoryInBackground(category, page, pageSize, cacheKey) {
    try {
      const [productsData, promotedData] = await Promise.all([
        this.fetchProductsByCategoryFromNetwork(category, page, pageSize),
        this.fetchPromotedProductsByCategoryFromNetwork(category)
      ]);
      
      if (productsData) {
        const combinedData = {
          products: productsData.results || [],
          promotedProducts: promotedData?.results || [],
          hasMore: !!productsData.next,
          totalCount: productsData.count || 0,
        };
        
        setMemoryCache(cacheKey, combinedData, CACHE_CONFIG.CATEGORY_TTL);
        setLocalCache(cacheKey, combinedData, CACHE_CONFIG.CATEGORY_TTL);}
    } catch (error) {}
  }

  /**
   * Fetch products by category name from network (missing method implementation)
   */
  static async fetchProductsByCategoryFromNetwork(categoryName, page = 1, pageSize = 30) {
    try {const params = new URLSearchParams();
      params.append('category_name', categoryName);
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());

      const url = `${config.backendUrl}/products/?${params}`;const response = await fetch(url);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('ProductService: Category products fetch failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();return data;
    } catch (error) {
      console.error('ProductService: fetchProductsByCategoryFromNetwork error:', error);
      throw error;
    }
  }

  /**
   * Fetch promoted products by category name from network (missing method implementation)
   */
  static async fetchPromotedProductsByCategoryFromNetwork(categoryName) {
    try {const url = `${config.backendUrl}/products/promoted/category/view/${encodeURIComponent(categoryName)}/`;const response = await fetch(url);

      if (!response.ok) {return { results: [] }; // Return empty results instead of throwing
      }

      const data = await response.json();
      const results = Array.isArray(data) ? data : data.results || [];return { results };
    } catch (error) {return { results: [] }; // Return empty results for promoted products errors
    }
  }

  /**
   * OPTIMIZED: Combined ALL products with promoted products
   * This is for the ProductsAll page that shows all products with province/city filtering
   */
  static async getAllProductsCombined(provinceId = '', cityId = '', page = 1, pageSize = 30) {
    const cacheKey = generateCacheKey('all_products_combined', { provinceId, cityId, page, pageSize });// Check memory cache first
    const memoryCached = getMemoryCache(cacheKey);
    if (memoryCached) {return { ...memoryCached, fromCache: true };
    }
    
    // Check localStorage cache
    const localCachedData = getLocalCache(cacheKey);
    if (localCachedData) {// Set in memory cache for faster future access
      setMemoryCache(cacheKey, localCachedData, CACHE_CONFIG.DASHBOARD_TTL);
      this.refreshAllProductsInBackground(provinceId, cityId, page, pageSize, cacheKey);
      return { ...localCachedData, fromCache: true };
    }

    try {
      // Use request deduplication for parallel fetching
      const [productsData, promotedData] = await Promise.all([
        this.requestWithDeduplication(
          `all_products_${provinceId}_${cityId}_p${page}`, 
          () => this.fetchAllProductsFromNetwork(provinceId, cityId, page, pageSize)
        ),
        this.requestWithDeduplication(
          `promoted_all_products`, 
          () => this.fetchPromotedProductsFromNetwork()
        )
      ]);
      
      // Group products by category and combine data
      const categories = this.groupProductsByCategory(productsData.results || []);
      const combinedData = {
        categories,
        promotedProducts: promotedData || [],
        totalProducts: productsData.results?.length || 0,
        hasMore: !!productsData.next,
        totalCount: productsData.count || 0
      };

      // Cache the result
      setMemoryCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);
      setLocalCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);return { ...combinedData, fromCache: false };
    } catch (error) {
      console.error('ProductService.getAllProductsCombined: Error fetching data', error);
      throw error;
    }
  }

  /**
   * Background refresh for all products data
   */
  static refreshAllProductsInBackground(provinceId, cityId, page, pageSize, cacheKey) {
    setTimeout(async () => {
      try {const [productsData, promotedData] = await Promise.all([
          this.fetchAllProductsFromNetwork(provinceId, cityId, page, pageSize),
          this.fetchPromotedProductsFromNetwork()
        ]);
        
        const categories = this.groupProductsByCategory(productsData.results || []);
        const combinedData = {
          categories,
          promotedProducts: promotedData || [],
          totalProducts: productsData.results?.length || 0,
          hasMore: !!productsData.next,
          totalCount: productsData.count || 0
        };

        setMemoryCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);
        setLocalCache(cacheKey, combinedData, CACHE_CONFIG.DASHBOARD_TTL);} catch (error) {}
    }, 100);
  }

  /**
   * Fetch ALL products from network (not dashboard, not province-specific)
   */
  static async fetchAllProductsFromNetwork(provinceId = '', cityId = '', page = 1, pageSize = 20) {
    const params = new URLSearchParams();
    if (provinceId) params.append('province', provinceId);
    if (cityId) params.append('city', cityId);
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());

    // Use /products/all/ endpoint which has 2-minute backend caching
    const url = `${config.backendUrl}/products/all/?${params}`;const response = await fetch(url);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ProductService: All products fetch failed:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();return data;
  }

  /**
   * OPTIMIZED: Fetch all provinces with caching (public endpoint, no auth required)
   */
  static async getPublicProvinces() {
    const startTime = Date.now();
    const cacheKey = 'public_provinces';try {
      // 1. Check memory cache first
      const cachedData = getMemoryCache(cacheKey);
      if (cachedData) {return { data: cachedData, fromCache: true, loadTime: Date.now() - startTime };
      }

      // 2. Check local storage cache (longer TTL for provinces)
      const localCachedData = getLocalCache(cacheKey);
      if (localCachedData) {
        setMemoryCache(cacheKey, localCachedData, CACHE_CONFIG.USER_DATA_TTL);return { data: localCachedData, fromCache: true, loadTime: Date.now() - startTime };
      }

      // 3. Fetch from network with deduplication
      const result = await deduplicateRequest(cacheKey, async () => {const response = await fetch(`${config.backendUrl}/products/public/provinces/`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch provinces`);
        }
        
        return response.json();
      });

      // Cache the results with longer TTL
      setMemoryCache(cacheKey, result, CACHE_CONFIG.USER_DATA_TTL);
      setLocalCache(cacheKey, result, CACHE_CONFIG.USER_DATA_TTL);return { data: result, fromCache: false, loadTime: Date.now() - startTime };
    } catch (error) {
      console.error('ProductService.getPublicProvinces: Error fetching provinces', error);
      return { data: [], fromCache: false, loadTime: Date.now() - startTime };
    }
  }

  /**
   * OPTIMIZED: Fetch cities by province with caching
   */
  static async getCitiesByProvince(provinceId) {
    if (!provinceId) return { data: [], fromCache: false, loadTime: 0 };
    
    const startTime = Date.now();
    const cacheKey = `cities_province_${provinceId}`;try {
      // 1. Check memory cache first
      const cachedData = getMemoryCache(cacheKey);
      if (cachedData) {return { data: cachedData, fromCache: true, loadTime: Date.now() - startTime };
      }

      // 2. Check local storage cache
      const localCachedData = getLocalCache(cacheKey);
      if (localCachedData) {
        setMemoryCache(cacheKey, localCachedData, CACHE_CONFIG.USER_DATA_TTL);return { data: localCachedData, fromCache: true, loadTime: Date.now() - startTime };
      }

      // 3. Fetch from network with deduplication
      const result = await deduplicateRequest(cacheKey, async () => {const response = await fetch(`${config.backendUrl}/products/public/provinces/${provinceId}/cities/`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch cities`);
        }
        
        return response.json();
      });

      // Cache the results
      setMemoryCache(cacheKey, result, CACHE_CONFIG.USER_DATA_TTL);
      setLocalCache(cacheKey, result, CACHE_CONFIG.USER_DATA_TTL);return { data: result, fromCache: false, loadTime: Date.now() - startTime };
    } catch (error) {
      console.error('ProductService.getCitiesByProvince: Error fetching cities', error);
      return { data: [], fromCache: false, loadTime: Date.now() - startTime };
    }
  }

  /**
   * Wrapper for deduplicateRequest to maintain consistency with calling pattern
   */
  static async requestWithDeduplication(key, requestFn) {
    return await deduplicateRequest(key, requestFn);
  }

  /**
   * Fetch dashboard products from network (main products listing)
   */
  static async fetchDashboardProductsFromNetwork(page = 1, pageSize = 30) {
    try {const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      // Use the /products/ endpoint which doesn't require authentication
      const url = `${config.backendUrl}/products/?${params}`;const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('ProductService: Dashboard products fetch failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();return data;
    } catch (error) {
      console.error('ProductService: fetchDashboardProductsFromNetwork error:', error);
      throw error;
    }
  }
  
  /**
   * Fetch promoted products from network
   */
  static async fetchPromotedProductsFromNetwork() {
    try {// Get the token, but don't require it
      const token = Cookies.get('authToken');
      const headers = token ? { Authorization: `Token ${token}` } : {};
      
      const url = `${config.backendUrl}/products/promoted/`;const response = await fetch(url, { headers });
      
      if (!response.ok) {// Return empty array instead of throwing
        return { results: [] };
      }
      
      const data = await response.json();
      
      // Handle different response formats
      const results = Array.isArray(data) ? data : data.results || [];
      
      // Filter out expired promotions
      const activePromotedProducts = filterActivePromotions(results);return { results: activePromotedProducts };
    } catch (error) {// Return empty array for promoted products errors
      return { results: [] };
    }
  }

  /**
   * Method to fetch products by category ID with caching and optimization
   */
  static async getProductsByCategoryIdCombined(categoryId, page = 1, pageSize = 30) {
    const cacheKey = `category_id_products_${categoryId}_${page}_${pageSize}`;
    
    try {
      // Check cache first
      const cachedData = getMemoryCache(cacheKey);
      if (cachedData) {
        return {
          ...cachedData,
          fromCache: true,
          loadTime: 0 // Cache hit, so no load time
        };
      }
  
      // Fetch from network
      const startTime = Date.now();
      const result = await ProductService.fetchProductsByCategoryIdFromNetwork(categoryId, page, pageSize);
      const loadTime = Date.now() - startTime;
  
      // Cache the result
      setMemoryCache(cacheKey, result, CACHE_CONFIG.CATEGORY_TTL);
  
      return {
        ...result,
        fromCache: false,
        loadTime
      };
    } catch (error) {
      console.error('Error in getProductsByCategoryIdCombined:', error);
      throw error;
    }
  }

  /**
   * Network method to fetch products by category ID
   */
  static async fetchProductsByCategoryIdFromNetwork(categoryId, page = 1, pageSize = 30) {
    try {
      // Use the correct API endpoint that filters by category_id
      const productsResponse = await fetch(`${config.backendUrl}/categories/minimal/${categoryId}/?page=${page}&page_size=${pageSize}`, {
        headers: { 'Content-Type': 'application/json' }
      });if (!productsResponse.ok) {
        const errorText = await productsResponse.text();
        console.error('ProductService: Category products API error:', {
          status: productsResponse.status,
          statusText: productsResponse.statusText,
          errorText
        });
        throw new Error(`Products API error: ${productsResponse.status}`);
      }

      const productsData = await productsResponse.json();return {
        products: productsData.results || [],
        promotedProducts: productsData.promoted_products || [],
        hasMore: !!productsData.next,
        totalCount: productsData.count || 0
      };
    } catch (error) {
      console.error('Error fetching category products by ID:', error);
      throw error;
    }
  }

  /**
   * Method to fetch promoted products by category ID
   */
  static async fetchPromotedProductsByCategoryIdFromNetwork(categoryId) {
    try {
      const response = await fetch(`${config.backendUrl}/products/promoted/?category=${categoryId}`, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {return [];
      }

      const data = await response.json();
      return data.results || data || [];
    } catch (error) {return [];
    }
  }

  /**
   * Fetch promoted products by main category ID from network
   */
  static async fetchPromotedProductsByMainCategoryFromNetwork(mainCategoryId) {
    try {// Fixed: Use the correct endpoint that accepts main_category_id as integer
      const url = `${config.backendUrl}/promoted/main-category/${mainCategoryId}/`;const response = await fetch(url);

      if (!response.ok) {return { results: [] }; // Return empty results instead of throwing
      }

      const data = await response.json();
      const results = Array.isArray(data) ? data : data.results || [];return { results };
    } catch (error) {return { results: [] }; // Return empty results for promoted products errors
    }
  }

  /**
   * Get products by main category ID for similar products section
   */
  static async getProductsByMainCategory(mainCategoryId, excludeProductId = null, page = 1, pageSize = 20) {
    const startTime = Date.now();
    const cacheKey = generateCacheKey('main_category_products', { mainCategoryId, excludeProductId, page, pageSize });try {
      // Check memory cache
      const cachedData = getMemoryCache(cacheKey);
      if (cachedData) {
        return { ...cachedData, fromCache: true, loadTime: Date.now() - startTime };
      }

      // Check local storage
      const localCachedData = getLocalCache(cacheKey);
      if (localCachedData) {
        setMemoryCache(cacheKey, localCachedData, CACHE_CONFIG.CATEGORY_TTL);
        return { ...localCachedData, fromCache: true, loadTime: Date.now() - startTime };
      }

      // Fetch from network
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      if (excludeProductId) {
        params.append('exclude', excludeProductId.toString());
      }

      const url = `${config.backendUrl}/products/main-category/${mainCategoryId}/?${params}`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();let products = [];
        
        // Handle different response formats
        if (data.results && Array.isArray(data.results)) {
          // Paginated response
          products = data.results;
        } else if (Array.isArray(data)) {
          // Direct array response - flatten if it's grouped by category
          for (const item of data) {
            if (item.products && Array.isArray(item.products)) {
              // Grouped by category format
              products.push(...item.products);
            } else {
              // Direct product format
              products.push(item);
            }
          }
        }
        
        const resultData = {
          products: products,
          hasMore: !!data.next,
          totalCount: data.count || products.length,
        };
        
        setMemoryCache(cacheKey, resultData, CACHE_CONFIG.CATEGORY_TTL);
        setLocalCache(cacheKey, resultData, CACHE_CONFIG.CATEGORY_TTL);
        
        return {
          ...resultData,
          fromCache: false,
          loadTime: Date.now() - startTime
        };
      }return { 
        products: [],
        hasMore: false,
        totalCount: 0,
        fromCache: false,
        loadTime: Date.now() - startTime
      };
    } catch (error) {
      console.error('Error in getProductsByMainCategory:', error);
      return { 
        products: [],
        hasMore: false,
        totalCount: 0,
        fromCache: false,
        loadTime: Date.now() - startTime
      };
    }
  }

  /**
   * OPTIMIZED: Enhanced getProduct method with caching and normalization
   * Similar to app's useProduct hook - provides better performance and reliability
   */
  static async getProduct(productId) {
    const startTime = Date.now();
    const cacheKey = `product_${productId}`;
    const cacheExpiryKey = `${cacheKey}_expiry`;

    // Normalizer function (same as app)
    const normalizeProduct = (p) => {
      if (!p || typeof p !== 'object') return p;
      const media = Array.isArray(p.media) ? p.media : [];
      const images = Array.isArray(p.images)
        ? p.images
        : (media.length > 0
          ? media.map((m, i) => ({
              id: m?.id ?? i + 1,
              type: m?.type || 'image',
              url: m?.url || '',
              image: m?.url || ''
            }))
          : []);

      const first_image = p.first_image || (media[0]?.url || images[0]?.url || images[0]?.image || '');

      return {
        ...p,
        media,
        images,
        first_image,
        category_name: p.category_name || p.category?.name || '',
        city_name: p.city_name || p.city?.name || '',
        owner_name: p.owner_name || p.owner_full_name || p.owner?.name || '',
        owner_mobile_number: p.owner_mobile_number || '',
      };
    };

    try {
      // Check local storage cache first (5 minutes TTL like app)
      const cachedData = getLocalCache(cacheKey);
      const cacheExpiry = localStorage.getItem(cacheExpiryKey);

      if (cachedData && cacheExpiry) {
        const expiryTime = parseInt(cacheExpiry, 10);
        const currentTime = Date.now();

        if (currentTime < expiryTime) {
          const parsedProduct = normalizeProduct(cachedData);

          // Check if product has media - if not, force network refresh
          const hasMedia = parsedProduct?.media && Array.isArray(parsedProduct.media) && parsedProduct.media.length > 0;
          if (!hasMedia) {
            console.log('Product cache missing media, fetching from network');
          } else {
            // Check for all required fields
            const missingEssentials = !parsedProduct?.category_name ||
                                    !parsedProduct?.city_name ||
                                    !parsedProduct?.owner_name ||
                                    parsedProduct?.owner_mobile_number === undefined ||
                                    parsedProduct?.main_category_id === undefined;
            if (!missingEssentials) {
              console.log('Returning cached product data');
              return {
                ...parsedProduct,
                fromCache: true,
                loadTime: Date.now() - startTime
              };
            }
          }
        }
      }

      // Fetch from network if cache is invalid
      console.log('Fetching product from network');
      const token = Cookies.get('authToken');
      const headers = token ? { Authorization: `Token ${token}` } : {};

      const response = await fetch(`${config.backendUrl}/products/view/${productId}/`, { headers });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('PRODUCT_NOT_FOUND');
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      const normalized = normalizeProduct(result);

      // Cache the result with 5-minute expiry
      setLocalCache(cacheKey, normalized, 5 * 60 * 1000); // 5 minutes
      localStorage.setItem(cacheExpiryKey, (Date.now() + 5 * 60 * 1000).toString());

      return {
        ...normalized,
        fromCache: false,
        loadTime: Date.now() - startTime
      };

    } catch (error) {
      console.error('ProductService.getProduct: Error', error);

      // Fallback to cache on network error (even if expired)
      try {
        const cachedData = getLocalCache(cacheKey);
        if (cachedData) {
          const parsedProduct = normalizeProduct(cachedData);
          const missingEssentials = !parsedProduct?.category_name || !parsedProduct?.city_name || !parsedProduct?.owner_name;
          if (!missingEssentials) {
            console.log('Returning expired cache due to network error');
            return {
              ...parsedProduct,
              fromCache: true,
              loadTime: Date.now() - startTime,
              fromExpiredCache: true
            };
          }
        }
      } catch (cacheError) {
        console.error('Cache fallback failed:', cacheError);
      }

      // Re-throw the original error if we can't provide cached data
      throw error;
    }
  }
}

// Export utility functions
export const likeProduct = async (productIdentifier) => {
  const token = Cookies.get('authToken');
  if (!token) {
    throw new Error('No authentication token found');
  }

  // Accept both UUID and numeric ID
  const response = await fetch(`${config.backendUrl}/products/like/${productIdentifier}/`, {
    method: 'POST',
    headers: {
      'Authorization': `Token ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to like product');
  }

  return response.json();
};

export const unlikeProduct = async (productIdentifier) => {
  const token = Cookies.get('authToken');
  if (!token) {
    throw new Error('No authentication token found');
  }

  // Accept both UUID and numeric ID
  const response = await fetch(`${config.backendUrl}/products/unlike/${productIdentifier}/`, {
    method: 'POST',
    headers: {
      'Authorization': `Token ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to unlike product');
  }

  return response.json();
};

/**
 * Invalidate product from all cache layers (call after deleting a product)
 * Prevents stale data from appearing in product lists
 */
export const invalidateProductFromAllCaches = (productId, userId = null) => {
  console.log(`🗑️ Invalidating product ${productId} from all caches`);
  
  try {
    // 1. Clear from memory cache - any keys containing this product ID
    const keysToDelete = [];
    memoryCache.forEach((value, key) => {
      if (key.includes(`product_${productId}`) || 
          key.includes(`products/${productId}`) ||
          key.includes(`products_${productId}`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => memoryCache.delete(key));
    console.log(`  ✓ Cleared ${keysToDelete.length} memory cache entries`);

    // 2. Clear dashboard cache (forces reload of product lists)
    const dashboardKeys = Array.from(memoryCache.keys()).filter(key => 
      key.includes('dashboard')
    );
    dashboardKeys.forEach(key => memoryCache.delete(key));
    console.log(`  ✓ Cleared ${dashboardKeys.length} dashboard cache entries`);

    // 3. Clear localStorage caches
    const localStorageKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes(`product_${productId}`) ||
        key.includes('dashboard') ||
        key.includes('user_products')
      )) {
        localStorageKeys.push(key);
      }
    }
    localStorageKeys.forEach(key => localStorage.removeItem(key));
    console.log(`  ✓ Cleared ${localStorageKeys.length} localStorage entries`);

    // 4. Clear user products cache if userId provided
    if (userId) {
      const userCacheKeys = [`user_products_${userId}`, `user_products_${userId}_expiry`];
      userCacheKeys.forEach(key => {
        memoryCache.delete(key);
        localStorage.removeItem(key);
      });
      console.log(`  ✓ Cleared user ${userId} products cache`);
    }

    console.log(`✅ Product ${productId} invalidated from all caches`);
  } catch (error) {
    console.error(`❌ Error invalidating product ${productId} from caches:`, error);
  }
};

export default ProductService;